import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/apiInterceptor';
import { useAuth } from './AuthContext';

export interface PricingPlan {
  tier: 'basic' | 'pro' | 'unlimited';
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  monthlyPriceId: string;
  yearlyPriceId: string;
  monthlyGenerations: number;
  features: string[];
}

export interface UsageStats {
  coursesGeneratedThisPeriod: number;
  totalCoursesGenerated: number;
  freeCourseUsed: boolean;
  monthlyLimit: number;
  canGenerate: boolean;
  limitReached: boolean;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
}

export interface SubscriptionStatus {
  subscriptionTier: string;
  subscriptionStatus: string;
  subscriptionExpiresAt?: string;
  subscriptionCancelAtPeriodEnd: boolean;
  usage: UsageStats;
}

interface SubscriptionContextType {
  // Subscription data
  subscriptionStatus: SubscriptionStatus | null;
  pricingPlans: PricingPlan[];
  loading: boolean;

  // Actions
  createCheckoutSession: (
    priceId: string,
    returnUrl?: string
  ) => Promise<{ url: string }>;
  createPortalSession: () => Promise<{ url: string }>;
  cancelSubscription: (cancelAtPeriodEnd?: boolean) => Promise<void>;
  reactivateSubscription: () => Promise<void>;
  refreshSubscriptionData: () => Promise<void>;

  // Helpers
  canGenerateCourse: () => boolean;
  getUsagePercentage: () => number;
  getRemainingGenerations: () => number;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined
);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      'useSubscription must be used within a SubscriptionProvider'
    );
  }
  return context;
};

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({
  children,
}) => {
  const { isAuthenticated, user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch pricing plans on mount
  useEffect(() => {
    fetchPricingPlans();
  }, []);

  // Fetch subscription status when user changes
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchSubscriptionStatus();
    } else {
      setSubscriptionStatus(null);
    }
  }, [isAuthenticated, user]);

  const fetchPricingPlans = async () => {
    try {
      const response = await api.get('/subscriptions/plans');
      setPricingPlans(response.data.data);
    } catch (error) {
      console.error('Error fetching pricing plans:', error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      const response = await api.get('/subscriptions/status');
      setSubscriptionStatus(response.data.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  const createCheckoutSession = async (priceId: string, returnUrl?: string) => {
    try {
      const response = await api.post('/subscriptions/create-checkout', {
        priceId,
        returnUrl,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  };

  const createPortalSession = async () => {
    try {
      const response = await api.post('/subscriptions/create-portal');
      return response.data.data;
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  };

  const cancelSubscription = async (cancelAtPeriodEnd: boolean = true) => {
    try {
      await api.post('/subscriptions/cancel', { cancelAtPeriodEnd });
      await fetchSubscriptionStatus(); // Refresh data
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  };

  const reactivateSubscription = async () => {
    try {
      await api.post('/subscriptions/reactivate');
      await fetchSubscriptionStatus(); // Refresh data
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      throw error;
    }
  };

  const refreshSubscriptionData = async () => {
    await fetchSubscriptionStatus();
  };

  // Helper functions
  const canGenerateCourse = (): boolean => {
    if (!subscriptionStatus) return false;
    return subscriptionStatus.usage.canGenerate;
  };

  const getUsagePercentage = (): number => {
    if (!subscriptionStatus || !subscriptionStatus.usage) return 0;

    const { usage } = subscriptionStatus;

    // Free tier: based on whether free course is used
    if (subscriptionStatus.subscriptionTier === 'free') {
      return usage.freeCourseUsed ? 100 : 0;
    }

    // Unlimited tier
    if (usage.monthlyLimit === -1) return 0;

    // Paid tiers
    return Math.min(
      (usage.coursesGeneratedThisPeriod / usage.monthlyLimit) * 100,
      100
    );
  };

  const getRemainingGenerations = (): number => {
    if (!subscriptionStatus || !subscriptionStatus.usage) return 0;

    const { usage } = subscriptionStatus;

    // Free tier
    if (subscriptionStatus.subscriptionTier === 'free') {
      return usage.freeCourseUsed ? 0 : 1;
    }

    // Unlimited tier
    if (usage.monthlyLimit === -1) return Infinity;

    // Paid tiers
    return Math.max(usage.monthlyLimit - usage.coursesGeneratedThisPeriod, 0);
  };

  const value: SubscriptionContextType = {
    subscriptionStatus,
    pricingPlans,
    loading,
    createCheckoutSession,
    createPortalSession,
    cancelSubscription,
    reactivateSubscription,
    refreshSubscriptionData,
    canGenerateCourse,
    getUsagePercentage,
    getRemainingGenerations,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
